import React, { useState, useEffect } from "react";
import { Box, Typography, Button, useMediaQuery, useTheme } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

import Background from "../../assets/image/png/background01.png";
import Logo from "../../assets/image/png/DronePlanet_Logo-removebg.png";

import dch from "../../assets/image/png/dch.png";
import dsk from "../../assets/image/png/dsk.png";
import marketplace from "../../assets/image/png/marketplace.png";
import services from "../../assets/image/png/services.png";
import carrers from "../../assets/image/png/carrers.png";
import dts from "../../assets/image/png/dts.png";
import gallery from "../../assets/image/png/gallery.png";
import training from "../../assets/image/png/training.png";

/* ═══════════════════════ DATA ═══════════════════════ */

const items = [
  {
    id: 0,
    title: "Drone Suvidha Kendra",
    image: dsk,
    link: "/dsk",
    description:
      "Drone Suvidha Kendra is a government-supported agricultural drone entrepreneurship program that enables rural youth to start a drone spraying business. With DGCA-approved training, structured support, and a scalable income model, it promotes modern and technology-driven farming across India. The program provides end-to-end assistance including drone procurement, maintenance support, and access to a growing network of agricultural clients, empowering young entrepreneurs to build sustainable livelihoods while modernizing Indian agriculture.",
  },
  {
    id: 1,
    title: "Careers",
    image: carrers,
    link: "/jobs-hire-page",
    description:
      "Explore exciting career opportunities in the rapidly growing drone industry. At Drone Planet, we empower passionate individuals with the right training, skills, and opportunities to build successful careers in drone technology, agriculture, and aerial services. Whether you are a fresh graduate looking to enter the drone ecosystem or an experienced professional seeking new challenges, our platform connects you with roles across operations, engineering, sales, and field services — helping you grow in one of India's fastest-evolving technology sectors.",
  },
  {
    id: 2,
    title: "Drone Tech Society",
    image: dts,
    link: "/drone-tech-society",
    description:
      "Drone Tech Society is a community-driven initiative focused on promoting drone technology, innovation, and knowledge sharing. It connects enthusiasts, professionals, and industry experts to collaborate, learn, and contribute to the growth of the drone ecosystem. Through workshops, hackathons, webinars, and hands-on building sessions, the society nurtures a culture of innovation and practical problem-solving — creating a pipeline of skilled talent ready to drive the next wave of drone adoption in agriculture, logistics, and public safety.",
  },
  {
    id: 3,
    title: "Drone City Haryana",
    image: dch,
    link: "/drone-city-haryana",
    description:
      "Drone City Haryana is an initiative aimed at building a strong ecosystem for drone innovation, training, and industry development. It promotes advanced drone technologies, supports startups, and creates new opportunities in agriculture, logistics, and aerial services. Positioned as a hub for drone manufacturing, testing, and R&D, Drone City brings together government agencies, academic institutions, and private enterprises to accelerate the adoption of unmanned aerial systems and establish Haryana as a national leader in drone technology infrastructure.",
  },
  {
    id: 4,
    title: "Training",
    image: training,
    link: "/training-courses/0",
    description:
      "Drone Planet offers DGCA-approved drone pilot training programs designed to equip individuals with professional flying skills, safety protocols, and industry-ready certification for careers in agriculture, surveying, and aerial services. Our comprehensive curriculum covers both theoretical knowledge and extensive hands-on flying experience, with specialized tracks for agricultural spraying, aerial mapping, infrastructure inspection, and emergency response — ensuring every graduate is fully prepared to operate safely and efficiently in real-world conditions.",
  },
  {
    id: 5,
    title: "Gallery",
    image: gallery,
    link: "/gallery",
    description:
      "Explore our gallery to see highlights from drone training programs, agricultural spraying operations, events, and innovations. It showcases the impact of drone technology and the work being done across our ecosystem. From large-scale field demonstrations and pilot graduation ceremonies to cutting-edge drone exhibitions and community outreach programs, our gallery captures the energy and momentum of India's growing drone revolution — offering a visual journey through the milestones that define Drone Planet's mission.",
  },
  {
    id: 6,
    title: "Services",
    image: services,
    link: "/daas-service-page",
    description:
      "Drone Planet provides a range of advanced drone services designed to support modern agriculture, training, and drone-based solutions. From agricultural spraying and aerial surveying to professional drone pilot training and technical support, our services help individuals and organizations adopt drone technology efficiently. We also offer consulting for drone fleet management, regulatory compliance assistance, custom payload integration, and data analytics for precision agriculture — delivering end-to-end solutions that maximize the value of drone technology for every client.",
  },
  {
    id: 7,
    title: "Marketplace",
    image: marketplace,
    link: "/product/all-drone",
    description:
      "Explore our marketplace for a wide range of drones, accessories, and drone-related equipment. Drone Planet connects buyers with reliable products and advanced technology to support agriculture, aerial services, and professional drone operations. Our curated catalog includes agricultural spraying drones, survey-grade UAVs, spare parts, batteries, chargers, and software solutions — all sourced from verified manufacturers and backed by technical support, warranty assistance, and expert guidance to help you choose the right tools for your specific use case.",
  },
];

/* ═══════════════════ GRID CONFIGS ═══════════════════ */

/* ──── DESKTOP: L-shape ──── */

const D_GAP = 1.5;
const D_CELL = (100 - 3 * D_GAP) / 4;
const dCol = (i) => i * (D_CELL + D_GAP);
const dRow = (i) => i * (D_CELL + D_GAP);

const desktopSlots = [
  { left: 0, top: 0, width: D_CELL * 3 + D_GAP * 2, height: D_CELL * 3 + D_GAP * 2 },
  { left: dCol(3), top: dRow(0), width: D_CELL, height: D_CELL },
  { left: dCol(3), top: dRow(1), width: D_CELL, height: D_CELL },
  { left: dCol(3), top: dRow(2), width: D_CELL, height: D_CELL },
  { left: dCol(0), top: dRow(3), width: D_CELL, height: D_CELL },
  { left: dCol(1), top: dRow(3), width: D_CELL, height: D_CELL },
  { left: dCol(2), top: dRow(3), width: D_CELL, height: D_CELL },
  { left: dCol(3), top: dRow(3), width: D_CELL, height: D_CELL },
];
const desktopRing = [0, 1, 2, 3, 7, 6, 5, 4];

/* ──── TABLET: 3×3 perimeter + center logo ──── */

const T_GAP = 2.5;
const T_COL = (100 - 2 * T_GAP) / 3;
const T_ROW = (100 - 2 * T_GAP) / 3;
const tCol = (i) => i * (T_COL + T_GAP);
const tRow = (i) => i * (T_ROW + T_GAP);

const tabletSlots = [
  { left: tCol(0), top: tRow(0), width: T_COL, height: T_ROW },
  { left: tCol(1), top: tRow(0), width: T_COL, height: T_ROW },
  { left: tCol(2), top: tRow(0), width: T_COL, height: T_ROW },
  { left: tCol(0), top: tRow(1), width: T_COL, height: T_ROW },
  { left: tCol(2), top: tRow(1), width: T_COL, height: T_ROW },
  { left: tCol(0), top: tRow(2), width: T_COL, height: T_ROW },
  { left: tCol(1), top: tRow(2), width: T_COL, height: T_ROW },
  { left: tCol(2), top: tRow(2), width: T_COL, height: T_ROW },
];
const tabletRing = [0, 1, 2, 4, 7, 6, 5, 3];

const tabletCenterSlot = {
  left: tCol(1),
  top: tRow(1),
  width: T_COL,
  height: T_ROW,
};

/* ──── MOBILE: 2×4 ──── */

const M_GAP = 3;
const M_COL = (100 - M_GAP) / 2;
const M_ROW = (100 - 3 * M_GAP) / 4;
const mCol = (i) => i * (M_COL + M_GAP);
const mRow = (i) => i * (M_ROW + M_GAP);

const mobileSlots = [
  { left: mCol(0), top: mRow(0), width: M_COL, height: M_ROW },
  { left: mCol(1), top: mRow(0), width: M_COL, height: M_ROW },
  { left: mCol(0), top: mRow(1), width: M_COL, height: M_ROW },
  { left: mCol(1), top: mRow(1), width: M_COL, height: M_ROW },
  { left: mCol(0), top: mRow(2), width: M_COL, height: M_ROW },
  { left: mCol(1), top: mRow(2), width: M_COL, height: M_ROW },
  { left: mCol(0), top: mRow(3), width: M_COL, height: M_ROW },
  { left: mCol(1), top: mRow(3), width: M_COL, height: M_ROW },
];
const mobileRing = [0, 1, 3, 5, 7, 6, 4, 2];

/* ═══════════════════ ANIMATION ═══════════════════ */

const positionTransition = { duration: 0.85, ease: [0.76, 0, 0.24, 1] };
const INTERVAL_MS = 3000;

/* ═══════════════════ BUTTON STYLE ═══════════════════ */

const primaryBtn = {
  borderRadius: "30px",
  textTransform: "none",
  fontWeight: 600,
  bgcolor: "#0d1b2a",
  boxShadow: "0 4px 14px rgba(13,27,42,0.35)",
  "&:hover": { bgcolor: "#1b2d45", boxShadow: "0 6px 20px rgba(13,27,42,0.45)" },
};

/* ═══════════════ ANIMATED IMAGE GRID ═══════════════ */

function AnimatedGrid({
  offset,
  slotList,
  ring,
  containerPadding,
  bigSlotIndex,
  tileFontSize,
  tileRadius,
  centerSlot,
}) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        "&::before": { content: '""', display: "block", paddingTop: containerPadding },
      }}
    >
      {centerSlot && (
        <Box
          sx={{
            position: "absolute",
            left: `${centerSlot.left}%`,
            top: `${centerSlot.top}%`,
            width: `${centerSlot.width}%`,
            height: `${centerSlot.height}%`,
            borderRadius: `${tileRadius}px`,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(10px)",
            border: "1.5px solid rgba(17,60,101,0.08)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
            zIndex: 2,
          }}
        >
          <img
            src={Logo}
            alt="Drone Planet"
            style={{ width: "75%", height: "auto", objectFit: "contain", opacity: 0.9 }}
          />
        </Box>
      )}

      {items.map((item, i) => {
        const ringPos = (i + offset) % items.length;
        const slotIdx = ring[ringPos];
        const slot = slotList[slotIdx];
        const isBig = bigSlotIndex !== undefined && slotIdx === bigSlotIndex;

        return (
          <motion.div
            key={item.id}
            layout
            animate={{
              left: `${slot.left}%`,
              top: `${slot.top}%`,
              width: `${slot.width}%`,
              height: `${slot.height}%`,
              borderRadius: isBig ? 24 : tileRadius,
            }}
            transition={positionTransition}
            style={{
              position: "absolute",
              overflow: "hidden",
              borderRadius: isBig ? 24 : tileRadius,
              zIndex: isBig ? 3 : 1,
              boxShadow: isBig
                ? "0 12px 40px rgba(0,0,0,0.18)"
                : "0 4px 12px rgba(0,0,0,0.10)",
              cursor: "pointer",
            }}
          >
            <Link to={item.link} style={{ display: "block", width: "100%", height: "100%", textDecoration: "none" }}>
              <img
                src={item.image}
                alt={item.title}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: isBig
                    ? "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.55) 100%)"
                    : "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.50) 100%)",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  pb: isBig ? 3 : 1.5,
                  px: 1,
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: isBig ? 22 : tileFontSize,
                    textAlign: "center",
                    textShadow: "0 1px 4px rgba(0,0,0,0.4)",
                    letterSpacing: 0.3,
                  }}
                >
                  {item.title}
                </Typography>
              </Box>
            </Link>
          </motion.div>
        );
      })}
    </Box>
  );
}

/* ═══════════════ ANIMATED TEXT SECTION ═══════════════ */

function AnimatedText({ featuredItem, titleSize, descSize }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Text area — grows to fill, vertically centers its content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={featuredItem.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <Typography
              sx={{
                fontSize: titleSize,
                fontWeight: 700,
                lineHeight: 1.15,
                color: "#0d1b2a",
              }}
            >
              {featuredItem.title}
            </Typography>

            <Typography
              sx={{
                mt: "clamp(12px, 2vh, 28px)",
                color: "#4a5568",
                fontSize: descSize,
                lineHeight: 1.75,
              }}
            >
              {featuredItem.description}
            </Typography>
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Button — always pinned at the bottom, never shifts */}
      <Box sx={{ flexShrink: 0, pb: "clamp(32px, 6vh, 72px)" }}>
        <Button
          component={Link}
          to={featuredItem.link}
          variant="contained"
          sx={{
            ...primaryBtn,
            px: 4,
            py: "clamp(8px, 1vh, 12px)",
            fontSize: "clamp(13px, 1.1vw, 16px)",
            textDecoration: "none",
          }}
        >
          Learn More
        </Button>
      </Box>
    </Box>
  );
}

/* ═══════════════════════ HERO ═══════════════════════ */

export default function Hero() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setOffset((prev) => (prev + 1) % items.length);
    }, INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const featuredItem = items[(items.length - (offset % items.length)) % items.length];

  /* ────── MOBILE ────── */
  if (isMobile) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          minHeight: "100dvh",
          backgroundImage: `url(${Background})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          flexDirection: "column",
          px: 2.5,
          pt: "80px",
          pb: 2,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <AnimatedText
            featuredItem={featuredItem}
            titleSize={24}
            descSize={13.5}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <AnimatedGrid
            offset={offset}
            slotList={mobileSlots}
            ring={mobileRing}
            containerPadding="190%"
            tileFontSize={11}
            tileRadius={14}
          />
        </Box>
      </Box>
    );
  }

  /* ────── TABLET ────── */
  if (isTablet) {
    return (
      <Box
        sx={{
          height: "100vh",
          height: "100dvh",
          backgroundImage: `url(${Background})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { sm: 3, md: 5 },
          pt: "80px",
          pb: 3,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1100,
            mx: "auto",
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            height: "calc(100vh - 130px)",
            gap: { sm: 3, md: 4 },
          }}
        >
          {/* LEFT: 50% text, vertically centered */}
          <Box
            sx={{
              flex: "0 0 50%",
              display: "flex",
              py: "clamp(16px, 3vh, 40px)",
              pr: { sm: 2, md: 3 },
            }}
          >
            <AnimatedText
              featuredItem={featuredItem}
              titleSize="clamp(26px, 3.5vw, 38px)"
              descSize="clamp(13px, 1.6vw, 17px)"
            />
          </Box>

          {/* RIGHT: 50% images, centered in its half */}
          <Box
            sx={{
              flex: "0 0 50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: "clamp(8px, 2vh, 24px)",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "min(480px, calc(100vh - 180px))",
                aspectRatio: "1",
              }}
            >
              <AnimatedGrid
                offset={offset}
                slotList={tabletSlots}
                ring={tabletRing}
                containerPadding="100%"
                tileFontSize={{ sm: 10, md: 12 }}
                tileRadius={14}
                centerSlot={tabletCenterSlot}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  /* ────── DESKTOP ────── */
  return (
    <Box
      sx={{
        height: "100vh",
        height: "100dvh",
        backgroundImage: `url(${Background})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: "clamp(24px, 5vw, 80px)",
        pt: "clamp(60px, 10vh, 90px)",
        pb: "clamp(12px, 2vh, 24px)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1400,
          mx: "auto",
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          height: "calc(100vh - clamp(80px, 12vh, 120px))",
          gap: "clamp(24px, 3vw, 56px)",
        }}
      >
        {/* LEFT 50%: text vertically centered with padding */}
        <Box
          sx={{
            flex: "0 0 50%",
            display: "flex",
            py: "clamp(16px, 3vh, 48px)",
            pr: "clamp(8px, 2vw, 32px)",
          }}
        >
          <AnimatedText
            featuredItem={featuredItem}
            titleSize="clamp(36px, 4.5vw, 72px)"
            descSize="clamp(15px, 1.35vw, 22px)"
          />
        </Box>

        {/* RIGHT 50%: images centered with equal padding */}
        <Box
          sx={{
            flex: "0 0 50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: "clamp(8px, 1.5vh, 24px)",
            pl: "clamp(8px, 1vw, 24px)",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "min(560px, calc(100vh - 180px))",
              aspectRatio: "1",
            }}
          >
            <AnimatedGrid
              offset={offset}
              slotList={desktopSlots}
              ring={desktopRing}
              containerPadding="100%"
              bigSlotIndex={0}
              tileFontSize={12}
              tileRadius={16}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
